import { useAct } from 'FlowBuilder/store'
import Button from 'Shared/components/Button'
import './SurveyPageForm.scoped.scss'
import PillEditor from 'Shared/components/PillEditor/PillEditor'
import useQuestionTranslate from 'FlowBuilder/hooks/useQuestionTranslate';
import QuestionSetting from './QuestionSetting';
import AnswerForm from './AnswerForm';
import AfterQuestionActions from './AfterQuestionActions';
import MultiSelectSort from 'Shared/components/SortableSelect';
import { getPostcodeCountriesData } from 'Shared/lib/postcodes';
import './QuestionForm.scoped.scss'

const ensureNumber = (n) => {
  const val = +n
  return isNaN(val) ? 0 : val
}

export default function QuestionForm({ question, mergeTags, questionConfig, nameDisabled = false }) {
  const questionId = question.id
  const [active, setActive] = useState(true)
  const allowedCountriesLabelRef = useRef(null)

  useEffect(() => {
    if (questionConfig.defaultRequired)
      setRequired(true)
    // Initialize postcode questions with user's country
    if (question.questionType === "postcode" && !question.settings?.allowedCountries && question.settings?.allCountries !== false) {
      const userCountry = window.ENV.COUNTRY?.toUpperCase() || 'GB'
      updateSettings({ 
        allCountries: false,
        allowedCountries: [userCountry]
      })
    }
  }, [])

  const act = useAct()
  const answers = useSel(s => _.values(s.answers.entities).filter(a => a.active && a.questionId === questionId))
  const { settings: { required } = { required: false } } = question

  const {
    language,
    translated: { name: questionName, ...translated },
    fallback: { name: questionNameFallback },
    textContentUpdater,
    flag
  } = useQuestionTranslate({ questionId });

  useEffect(() => {
    if (!question.question && questionConfig?.defaultLabel) {
      updateQuestion({ question: questionConfig.defaultLabel })
    }
  }, [question.question, questionConfig?.defaultLabel, questionId])

  function updateQuestion(data) {
    act.questions.upsert(questionId, data)
  }

  function setSetting(key, value) {
    act.questions.upsertSettings(questionId, { [key]: value })
  }

  function updateSettings(settings) {
    updateQuestion({ settings: { ...question.settings, ...settings } })
  }

  function setRequired(value) {
    updateSettings({ required: value })
  }

  const handleAllCountriesChange = (isAllCountries) => {
    if (isAllCountries) {
      updateSettings({ allCountries: true })
    } else {
      const currentAllowed = question.settings?.allowedCountries || []
      const userCountry = window.ENV.COUNTRY?.toUpperCase() || 'GB'

      // If the list is currently empty, add the user's country as default
      const defaultCountries = currentAllowed.length === 0 && !currentAllowed.includes(userCountry)
        ? [userCountry]
        : currentAllowed

      updateSettings({ 
        allCountries: false,
        allowedCountries: defaultCountries
      })
    }
  }

  const handleAllowedCountriesChange = (selectedCountries) => {
    const countryValues = selectedCountries && selectedCountries.length > 0 
      ? selectedCountries.map(option => option.value)
      : []
    
    updateSettings({ allowedCountries: countryValues })
  }

  const getCountryOptions = () => {
    const postcodeCountries = getPostcodeCountriesData()
    return postcodeCountries.map(country => ({
      value: country.ISO,
      label: country.Country
    }))
  }

  const getSelectedCountries = () => {
    const allowedCountries = question.settings?.allowedCountries || []
    const countryOptions = getCountryOptions()
    return countryOptions.filter(option => 
      allowedCountries.includes(option.value)
    )
  }

  const classNames = ['box question-form margin-bottom', `question-type-${question.questionType}`]
  if (active) {
    classNames.push('active')
  }

  const isAllCountries = !!(question.settings?.allCountries)

  return <div className={classNames.join(' ')}>
    <div className="flex-center gap-10">
      <div className="fake-input flex-center">
        <span>{questionConfig.icon()}</span>
        {questionConfig.name}
      </div>
      {questionConfig.multiline ?
        <span className="grow"></span> :
        (
          (active && nameDisabled !== true) ? (
            <PillEditor
              className="grow"
              initialValue={questionName}
              placeholder={questionNameFallback || questionConfig.defaultLabel}
              onChange={(text) => textContentUpdater('name', text)}
              availableTags={mergeTags}
              condensed
              singleLine
              key={language}
            />
          ) : (
            nameDisabled !== true ? (
              <div className="fake-input grow">{questionName || questionNameFallback || question.question || questionConfig.defaultLabel}</div>
            ) : (
             <div className={"grow disabled"}></div>
            )
          )
        )
      }
      {flag}
      {active && <Button naked square-button onClick={() => setActive(false)}><SvgIconsUpCaret /></Button>}
      {!active && <Button naked square-button onClick={() => setActive(true)}><SvgIconsDownCaret /></Button>}
    </div>

    {active &&
      <>
        {questionConfig.showRequired &&
          <div className='field'>
            <label className="flex-center gap-10">
              <input type='checkbox' checked={!!required} onChange={e => setSetting('required', e.target.checked)} />
              { questionConfig.requiredConfigText ?? 'Required' }
            </label>
          </div>
        }

        <div className="field">
          {questionConfig.multiline &&
            <PillEditor
              className="grow multiline"
              initialValue={questionName}
              placeholder={questionNameFallback}
              onChange={(text) => textContentUpdater('name', text)}
              availableTags={mergeTags}
              key={language} />
          }
        </div>

        {question.questionType === "score" &&
          <>
            <div className="score-settings">
              <div className="score-field">
                <label>Min</label>
                <input
                  name="min"
                  type="text"
                  placeholder="0"
                  value={question.settings?.min ?? ''}
                  onChange={(e) => updateSettings({ min: ensureNumber(e.target.value) })}
                />
              </div>
              <div className="score-field">
                <label>Max</label>
                <input
                  name="max"
                  type="text"
                  placeholder="10"
                  value={question.settings?.max ?? ''}
                  onChange={(e) => updateSettings({ max: ensureNumber(e.target.value) })}
                />
              </div>
              <div className="score-field">
                <label>Default</label>
                <input
                  name="default"
                  type="text"
                  value={question.settings?.default}
                  onChange={(e) => updateSettings({ default: ensureNumber(e.target.value) })}
                />
              </div>
            </div>
            <div className="score-settings">
              <div className="score-field score-field-wide">
                <label>Low score label {flag}</label>
                <input
                  name="score_label_left"
                  type="text"
                  placeholder="e.g. Bad"
                  value={translated.scoreLabelLeft || ''}
                  onChange={(e) => textContentUpdater('scoreLabelLeft', e.target.value)}
                />
              </div>
              <div className="score-field score-field-wide">
                <label>High score label {flag}</label>
                <input
                  name="score_label_right"
                  type="text"
                  placeholder="e.g. Good"
                  value={translated.scoreLabelRight || ''}
                  onChange={(e) => textContentUpdater('scoreLabelRight', e.target.value)}
                />
              </div>
            </div>
            <p className="bold smaller dark-grey">These labels are optional and will appear above the score buttons</p>
          </>
        }

        {questionConfig.hasAnswers &&
          <>
            {answers.map(a => <AnswerForm key={a.id} answerId={a.id} />)}
            <Button naked onClick={() => act.answers.add({ questionId })}>Add option</Button>
          </>
        }

        {question.questionType === "postcode" && 
          <div className='field'>
            <label className="flex-center gap-10 margin-bottom">
              <input 
                type='checkbox' 
                checked={isAllCountries} 
                onChange={(e) => handleAllCountriesChange(e.target.checked)} 
              />
              Allow all countries
            </label>
            
            <div className={`allowed-countries-selector ${isAllCountries ? 'disabled' : ''}`}>
              <label ref={allowedCountriesLabelRef} className="margin-bottom half">
                Allowed countries:
              </label>
              <MultiSelectSort
                value={getSelectedCountries()}
                onChange={handleAllowedCountriesChange}
                options={getCountryOptions()}
                placeholder="Select countries..."
                className="countries-multiselect"
                isDisabled={isAllCountries}
                onMenuOpen={() => {
                  if (!isAllCountries) {
                    setTimeout(() => {
                      const el = allowedCountriesLabelRef.current;
                      if (el && el.scrollIntoView) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
                      }
                    }, 50);
                  }
                }}
              />
            </div>
          </div>
        }

        <AfterQuestionActions question={question} updateQuestion={updateQuestion} />
        {(questionConfig.settings || []).filter(setting => setting.showIf ? setting.showIf(question.settings) : true).map(setting => <QuestionSetting key={setting.key} setting={setting} question={question} />)}
      </>
    }
  </div>
}
